/**
 * BoardingPass.js
 *
 * The Right Brother Airways boarding pass for Flight YJ030, the same for every guest
 * except their name, PNR, seat, origin and accent colour.
 * Two designs: a wide 1040×400 pass with a stub, and a tall 358×648 one for phones.
 * Each is drawn at its fixed size and scaled to fit the screen.
 * Private fields arrive from the backend already masked ("•••") for anyone but the owner.
 */

import React, { useLayoutEffect, useState } from "react";

// Wide pass: whole drawing, and the ticket inside it
const WIDTH = 1040;
const HEIGHT = 400;
const PASS_WIDTH = 1000;

// Phone pass: whole drawing (just the ticket plus space above and below), the ticket's width,
// and the widest it gets on screen. On phones it fills the screen, less an 8px margin each side.
const MOBILE_HEIGHT = 696;
const MOBILE_PASS_WIDTH = 358;
const MOBILE_MAX_PASS_WIDTH = 420;

// Screens narrower than this get the phone pass
const MOBILE_BREAKPOINT = 640;

const MASK = "•••";
const MASK_GREY = "#9AA1AD";

const INK = "#1B2A41";
const MUTED = "#5B6577";
const PAPER = "#FBF7EF";
const BACKDROP = "#E9E4DA";
const DASH = "#B7BDC7";

const MONO = "'JetBrains Mono', monospace";
const SANS = "'Space Grotesk', system-ui, sans-serif";

const MAP_URL = "https://maps.app.goo.gl/UE3PaJrsC7AuttCB8";

// Barcode bars: [thickness, gap after], in px. The phone pass draws them upright, 1.6× thicker with 1.4× the gap.
const BARCODE = [
    [3, 2], [1, 3], [2, 2], [1, 4], [4, 2], [2, 2], [1, 3], [3, 2], [1, 2], [1, 4], [2, 2], [4, 3],
    [1, 2], [2, 2], [3, 3], [1, 2], [1, 4], [3, 2], [2, 2], [1, 3], [4, 2], [1, 2], [2, 4], [1, 2],
    [3, 3], [2, 2], [1, 2], [1, 3], [4, 2], [2, 4], [1, 2], [3, 2], [1, 3], [2, 2], [1, 2], [2, 2],
];

const PLANE_PATH = "M17.8 19.2 16 11l3.5-3.5C21 6 21.5 4 21 3c-1-.5-3 0-4.5 1.5L13 8 4.8 6.2c-.5-.1-.9.1-1.1.5l-.3.5c-.2.5-.1 1 .3 1.3L9 12l-2 3H4l-1 1 3 2 2 3 1-1v-3l3-2 3.5 5.3c.3.4.8.5 1.3.3l.5-.2c.4-.3.6-.7.5-1.2z";

const labelStyle = { fontSize: 11, fontWeight: 700, letterSpacing: "0.18em", color: MUTED };
const stubLabelStyle = { fontSize: 10, fontWeight: 700, letterSpacing: "0.14em", color: MUTED };
const bigValueStyle = { fontFamily: MONO, fontSize: 30, fontWeight: 700, lineHeight: 1.1 };
const stubValueStyle = { fontFamily: MONO, fontSize: 17, fontWeight: 700, whiteSpace: "nowrap" };
const mapLinkStyle = { color: "inherit", textDecoration: "none" };

/**
 * A plane icon
 * @param {Number} size     -   Width and height in px
 * @param {String} colour   -   Stroke colour
 * @param {Object} style    -   Extra styles
 */
const Plane = ({ size, colour, style }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={colour} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={style} aria-hidden="true">
        <path d={PLANE_PATH} />
    </svg>
);

/**
 * One word per line, e.g. "NEW YORK" → NEW / YORK, like BAHUBALI / ENCLAVE
 * @param {String} text     -   e.g. 'NEW YORK'
 */
const WordPerLine = ({ text }) => text.split(" ").map((word, index) => (
    <React.Fragment key={index}>
        {index > 0 && <br />}
        {word}
    </React.Fragment>
));

/**
 * Which pass fits the screen, and how much to shrink it, without changing its layout
 * @returns {Object}    -   { mobile, scale (at most 1), passWidth: the ticket's width on screen, for matching other boxes to it }
 */
export const usePassLayout = () => {
    const [layout, setLayout] = useState({ mobile: false, scale: 1, passWidth: PASS_WIDTH });
    useLayoutEffect(() => {
        const fit = () => {
            const mobile = window.innerWidth < MOBILE_BREAKPOINT;
            const scale = mobile
                ? Math.min(window.innerWidth - 16, MOBILE_MAX_PASS_WIDTH) / MOBILE_PASS_WIDTH
                : Math.min(window.innerWidth - 16, WIDTH) / WIDTH;
            setLayout({ mobile, scale, passWidth: (mobile ? MOBILE_PASS_WIDTH : PASS_WIDTH) * scale });
        };
        fit();
        window.addEventListener("resize", fit);
        return () => window.removeEventListener("resize", fit);
    }, []);
    return layout;
};

/**
 * The wide pass, with a tear-off stub on the right
 */
const WidePass = ({ pass, scale }) => {

    const accent = pass.colour;
    const name = pass.fullName.toUpperCase();
    const fromCity = pass.fromCity.toUpperCase();

    return (
        <div style={{ width: "100%", overflow: "hidden", display: "flex", justifyContent: "center", padding: "24px 0", boxSizing: "border-box", height: HEIGHT * scale + 48 }}>
            <div
                style={{ width: WIDTH, height: HEIGHT, transformOrigin: "top left", flexShrink: 0, transform: `scale(${scale})`, marginRight: WIDTH * scale - WIDTH }}
            >
                <div style={{ width: WIDTH, height: HEIGHT, boxSizing: "border-box", padding: 20, fontFamily: SANS, color: INK, background: BACKDROP, textAlign: "left" }}>
                    <div style={{ position: "relative", width: PASS_WIDTH, height: 360, display: "flex", background: PAPER, borderRadius: 22, overflow: "hidden", boxShadow: "0 10px 30px rgba(27,42,65,0.18)" }}>

                        {/* ---------- Main ticket ---------- */}
                        <div style={{ width: 730, height: 360, display: "flex", flexDirection: "column" }}>
                            <div style={{ height: 76, background: accent, display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0 36px", boxSizing: "border-box", color: PAPER }}>
                                <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
                                    <Plane size={30} colour={PAPER} />
                                    <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
                                        <div style={{ fontSize: 24, fontWeight: 700, letterSpacing: "0.2em", lineHeight: 1 }}>RIGHT BROTHER AIRWAYS</div>
                                        <div style={{ fontSize: 11, fontWeight: 500, letterSpacing: "0.16em", opacity: 0.9 }}>DRINK. EAT. FLY.</div>
                                    </div>
                                </div>
                            </div>

                            <div style={{ flexGrow: 1, display: "flex", padding: "26px 36px 22px", boxSizing: "border-box", gap: 30 }}>
                                <div style={{ flexGrow: 1, display: "flex", flexDirection: "column", justifyContent: "space-between" }}>
                                    <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between" }}>
                                        <div>
                                            <div style={labelStyle}>FROM</div>
                                            <div style={{ fontFamily: MONO, fontSize: 58, fontWeight: 700, lineHeight: 1, color: accent }}>{pass.fromCode}</div>
                                            <div style={{ fontSize: 14, fontWeight: 500, letterSpacing: "0.08em" }}>{fromCity}</div>
                                        </div>
                                        <div style={{ display: "flex", alignItems: "center", gap: 8, paddingBottom: 26, color: "#8A93A3" }}>
                                            <div style={{ width: 70, borderTop: `2px dashed ${DASH}` }} />
                                            <Plane size={26} colour={accent} style={{ transform: "rotate(45deg)" }} />
                                            <div style={{ width: 70, borderTop: `2px dashed ${DASH}` }} />
                                        </div>
                                        <div style={{ textAlign: "right" }}>
                                            <div style={labelStyle}>TO</div>
                                            <div style={{ fontFamily: MONO, fontSize: 58, fontWeight: 700, lineHeight: 1, color: accent }}>BAH</div>
                                            <div style={{ fontSize: 14, fontWeight: 500, letterSpacing: "0.08em", whiteSpace: "nowrap" }}>
                                                <a href={MAP_URL} target="_blank" rel="noopener noreferrer" style={mapLinkStyle}>BAHUBALI ENCLAVE</a>
                                            </div>
                                        </div>
                                    </div>

                                    <div style={{ display: "grid", gridTemplateColumns: "repeat(4, minmax(0, 1fr))", gap: "14px 20px" }}>
                                        <div style={{ gridColumn: "span 2" }}><div style={labelStyle}>PASSENGER</div><div style={{ fontSize: 20, fontWeight: 700, letterSpacing: "0.04em" }}>{name}</div></div>
                                        <div><div style={labelStyle}>FLIGHT</div><div style={{ fontFamily: MONO, fontSize: 20, fontWeight: 700 }}>YJ030</div></div>
                                        <div><div style={labelStyle}>DATE</div><div style={{ fontFamily: MONO, fontSize: 20, fontWeight: 700 }}>30 OCT</div></div>
                                        <div><div style={labelStyle}>GATE</div><div style={bigValueStyle}>26</div></div>
                                        <div><div style={labelStyle}>BOARDING</div><div style={bigValueStyle}>20:00</div></div>
                                        <div><div style={labelStyle}>SEAT</div><div style={{ ...bigValueStyle, color: accent }}>{pass.seat}</div></div>
                                        <div><div style={labelStyle}>PNR</div><div style={bigValueStyle}>{pass.pnr}</div></div>
                                    </div>

                                    <div style={{ fontSize: 11, fontWeight: 500, letterSpacing: "0.12em", color: MUTED }}>DRESS CODE: PILOT / CABIN CREW / TOURIST (ACCESSORIZE WELL)</div>
                                </div>

                                <div style={{ width: 46, display: "flex", flexDirection: "column", justifyContent: "center" }} aria-hidden="true">
                                    {BARCODE.map(([height, gap], index) => (
                                        <div key={index} style={{ width: 46, height, marginBottom: gap, background: INK }} />
                                    ))}
                                </div>
                            </div>
                        </div>

                        {/* ---------- Perforation ---------- */}
                        <div style={{ position: "relative", width: 0, height: 360, borderLeft: "2px dashed #C9C2B4" }}>
                            <div style={{ position: "absolute", top: -18, left: -19, width: 36, height: 36, borderRadius: "50%", background: BACKDROP }} />
                            <div style={{ position: "absolute", bottom: -18, left: -19, width: 36, height: 36, borderRadius: "50%", background: BACKDROP }} />
                        </div>

                        {/* ---------- Stub ---------- */}
                        <div style={{ flexGrow: 1, display: "flex", flexDirection: "column" }}>
                            <div style={{ height: 76, background: accent, display: "flex", alignItems: "center", justifyContent: "center", color: PAPER, fontSize: 13, fontWeight: 700, letterSpacing: "0.24em", whiteSpace: "nowrap" }}>BOARDING PASS</div>
                            <div style={{ flexGrow: 1, padding: "20px 28px", boxSizing: "border-box", display: "flex", flexDirection: "column", justifyContent: "space-between" }}>
                                {/* Fixed height and top-aligned, so a two-line city never moves anything else */}
                                <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", height: 45 }}>
                                    <div>
                                        <div style={{ fontFamily: MONO, fontSize: 28, fontWeight: 700, lineHeight: 1 }}>{pass.fromCode}</div>
                                        <div style={{ fontSize: 10, fontWeight: 500, letterSpacing: "0.04em", lineHeight: 1.3, whiteSpace: "nowrap", marginTop: 4 }}><WordPerLine text={fromCity} /></div>
                                    </div>
                                    <div style={{ width: 36, borderTop: `2px dashed ${DASH}`, marginTop: 13 }} />
                                    <div style={{ textAlign: "right" }}>
                                        <div style={{ fontFamily: MONO, fontSize: 28, fontWeight: 700, lineHeight: 1, color: accent }}>BAH</div>
                                        <div style={{ fontSize: 10, fontWeight: 500, letterSpacing: "0.04em", lineHeight: 1.3, marginTop: 4, maxWidth: 64, marginLeft: "auto" }}>
                                            <a href={MAP_URL} target="_blank" rel="noopener noreferrer" style={mapLinkStyle}>BAHUBALI ENCLAVE</a>
                                        </div>
                                    </div>
                                </div>
                                <div style={{ display: "flex", justifyContent: "space-between", gap: 12 }}>
                                    <div><div style={stubLabelStyle}>PASSENGER</div><div style={{ fontSize: 16, fontWeight: 700, letterSpacing: "0.03em", whiteSpace: "nowrap" }}>{name}</div></div>
                                    <div style={{ textAlign: "right" }}><div style={stubLabelStyle}>FLIGHT</div><div style={{ fontFamily: MONO, fontSize: 16, fontWeight: 700, whiteSpace: "nowrap" }}>YJ030</div></div>
                                </div>
                                <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
                                    <div><div style={stubLabelStyle}>GATE</div><div style={stubValueStyle}>26</div></div>
                                    <div><div style={stubLabelStyle}>TIME</div><div style={stubValueStyle}>20:00</div></div>
                                    <div style={{ textAlign: "right" }}><div style={stubLabelStyle}>SEAT</div><div style={{ ...stubValueStyle, color: accent }}>{pass.seat}</div></div>
                                </div>
                                <div style={{ fontFamily: MONO, fontSize: 11, fontWeight: 500, letterSpacing: "0.04em", whiteSpace: "nowrap", color: MUTED }}>30 OCT 2026 · NOW BOARDING</div>
                            </div>
                        </div>

                    </div>
                </div>
            </div>
        </div>
    );
};

const mobileLabelStyle = { fontSize: 10, fontWeight: 700, letterSpacing: "0.16em", color: MUTED };
const mobileValueStyle = { fontFamily: MONO, fontSize: 18, fontWeight: 700 };

/**
 * The tall pass for phones. Masked fields are grey, like the design's locked state.
 */
const MobilePass = ({ pass, scale, qr }) => {
    const accent = pass.colour;
    const privateColour = (value, colour) => (value === MASK ? MASK_GREY : colour);

    return (
        <div style={{ width: "100%", overflow: "hidden", display: "flex", justifyContent: "center", height: MOBILE_HEIGHT * scale }}>
            <div style={{ width: MOBILE_PASS_WIDTH, height: MOBILE_HEIGHT, transformOrigin: "top left", flexShrink: 0, transform: `scale(${scale})`, marginRight: MOBILE_PASS_WIDTH * scale - MOBILE_PASS_WIDTH }}>
                <div style={{ width: MOBILE_PASS_WIDTH, height: MOBILE_HEIGHT, boxSizing: "border-box", padding: "24px 0", fontFamily: SANS, color: INK, background: BACKDROP, textAlign: "left" }}>
                    <div style={{ width: MOBILE_PASS_WIDTH, height: 648, display: "flex", flexDirection: "column", background: PAPER, borderRadius: 24, overflow: "hidden", boxShadow: "0 10px 30px rgba(27,42,65,0.18)" }}>

                        {/* ---------- Header ---------- */}
                        <div style={{ height: 96, flexShrink: 0, background: accent, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 6, color: PAPER }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                                <Plane size={22} colour={PAPER} />
                                <div style={{ fontSize: 17, fontWeight: 700, letterSpacing: "0.16em", whiteSpace: "nowrap" }}>RIGHT BROTHER AIRWAYS</div>
                            </div>
                            <div style={{ fontSize: 10, fontWeight: 500, letterSpacing: "0.2em", opacity: 0.9 }}>DRINK. EAT. FLY.</div>
                        </div>

                        <div style={{ flexGrow: 1, padding: "24px 24px 20px", boxSizing: "border-box", display: "flex", flexDirection: "column", justifyContent: "flex-start", gap: 28 }}>
                            {/* Fixed height and top-aligned, so a two-line city never moves anything else */}
                            <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", height: 92 }}>
                                <div>
                                    <div style={mobileLabelStyle}>FROM</div>
                                    <div style={{ fontFamily: MONO, fontSize: 44, fontWeight: 700, lineHeight: 1.05, color: privateColour(pass.fromCode, accent) }}>{pass.fromCode}</div>
                                    <div style={{ fontSize: 11, fontWeight: 500, letterSpacing: "0.06em", lineHeight: 1.3, maxWidth: 96, color: privateColour(pass.fromCity, INK) }}><WordPerLine text={pass.fromCity.toUpperCase()} /></div>
                                </div>
                                <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 34 }}>
                                    <div style={{ width: 22, borderTop: `2px dashed ${DASH}` }} />
                                    <Plane size={22} colour={accent} style={{ transform: "rotate(45deg)" }} />
                                    <div style={{ width: 22, borderTop: `2px dashed ${DASH}` }} />
                                </div>
                                <div style={{ textAlign: "right" }}>
                                    <div style={mobileLabelStyle}>TO</div>
                                    <div style={{ fontFamily: MONO, fontSize: 44, fontWeight: 700, lineHeight: 1.05, color: accent }}>BAH</div>
                                    <div style={{ fontSize: 11, fontWeight: 500, letterSpacing: "0.06em", lineHeight: 1.3, maxWidth: 96, marginLeft: "auto" }}>
                                        <a href={MAP_URL} target="_blank" rel="noopener noreferrer" style={mapLinkStyle}>BAHUBALI ENCLAVE</a>
                                    </div>
                                </div>
                            </div>

                            <div style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: "18px 12px" }}>
                                <div style={{ gridColumn: "span 2" }}><div style={mobileLabelStyle}>PASSENGER</div><div style={{ fontSize: 18, fontWeight: 700, letterSpacing: "0.03em", whiteSpace: "nowrap" }}>{pass.fullName.toUpperCase()}</div></div>
                                <div style={{ textAlign: "right" }}><div style={mobileLabelStyle}>FLIGHT</div><div style={mobileValueStyle}>YJ030</div></div>
                                <div><div style={mobileLabelStyle}>DATE</div><div style={{ ...mobileValueStyle, whiteSpace: "nowrap" }}>30 OCT</div></div>
                                <div><div style={mobileLabelStyle}>GATE</div><div style={mobileValueStyle}>26</div></div>
                                <div style={{ textAlign: "right" }}><div style={mobileLabelStyle}>BOARDING</div><div style={mobileValueStyle}>20:00</div></div>
                                <div><div style={mobileLabelStyle}>SEAT</div><div style={{ fontFamily: MONO, fontSize: 26, fontWeight: 700, lineHeight: 1.1, color: privateColour(pass.seat, accent) }}>{pass.seat}</div></div>
                                <div style={{ gridColumn: "span 2", textAlign: "right" }}><div style={mobileLabelStyle}>PNR</div><div style={{ fontFamily: MONO, fontSize: 26, fontWeight: 700, lineHeight: 1.1 }}>{pass.pnr}</div></div>
                            </div>

                            <div style={{ fontSize: 10, fontWeight: 500, letterSpacing: "0.08em", lineHeight: 1.5, color: MUTED, textAlign: "center" }}>DRESS CODE: PILOT / CABIN CREW / TOURIST<br />(ACCESSORIZE WELL)</div>
                        </div>

                        {/* ---------- Perforation ---------- */}
                        <div style={{ position: "relative", height: 0, flexShrink: 0, margin: "0 22px", borderTop: "2px dashed #C9C2B4" }}>
                            <div style={{ position: "absolute", top: -17, left: -40, width: 32, height: 32, borderRadius: "50%", background: BACKDROP }} />
                            <div style={{ position: "absolute", top: -17, right: -40, width: 32, height: 32, borderRadius: "50%", background: BACKDROP }} />
                        </div>

                        {/* ---------- Barcode (or a real QR code, e.g. on the PDF boarding pass) ---------- */}
                        <div style={{ height: 168, flexShrink: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: qr ? 8 : 14 }}>
                            {qr || (
                                <div style={{ height: 76, display: "flex", alignItems: "stretch" }} aria-hidden="true">
                                    {BARCODE.map(([thickness, gap], index) => (
                                        <div key={index} style={{ width: thickness * 1.6, marginRight: index === BARCODE.length - 1 ? 0 : gap * 1.4, background: INK }} />
                                    ))}
                                </div>
                            )}
                            <div style={{ fontFamily: MONO, fontSize: 11, fontWeight: 500, letterSpacing: "0.08em", whiteSpace: "nowrap", color: MUTED }}>30 OCT 2026 · NOW BOARDING</div>
                        </div>

                    </div>
                </div>
            </div>
        </div>
    );
};

/**
 * @param {Object} pass     -   { pnr, fullName, seat, fromCode, fromCity, colour }, from the backend
 * @param {Object} layout   -   From usePassLayout()
 * @param {ReactNode} qr    -   Phone pass only: a QR code in place of the decorative barcode
 * @returns {JSX.Element}
 */
const BoardingPass = ({ pass, layout, qr }) => (
    layout.mobile ? <MobilePass pass={pass} scale={layout.scale} qr={qr} /> : <WidePass pass={pass} scale={layout.scale} />
);

export default BoardingPass;
